'use client';

import {
  OptimizelyContext,
  OptimizelyProvider,
  createInstance,
  useDecision,
} from '@optimizely/react-sdk';
import { Auth } from '@justgiving/auth';
import { ProductNames } from '../../constants/ProductNames';
import { PropsWithChildren, useContext, useEffect } from 'react';
import clientConfig from '../../client/config';
import { isMobileDevice } from '../../utils/mobile';
import { usePage } from './PageProvider';
import { useAuth } from './AuthProvider';
import { getUserId } from '../../lib/optimizely';
import { v4 } from 'uuid';
import { cookies } from '../../utils/cookies';
import { isJson } from '../../utils/isJson';

export const optimizelyClient = createInstance({
  sdkKey: clientConfig.optimizelyFullstackKey,
  eventBatchSize: 10,
  eventFlushInterval: 300,
});

export const ExperimentProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const {
    charity,
    campaign,
    product: { name: productName },
  } = usePage();
  const auth = useAuth();

  const getDeviceType = () => {
    if (typeof window !== 'undefined')
      return isMobileDevice() ? 'Mobile' : 'Desktop';

    return 'Unknown';
  };

  const getCauseType = () => {
    if (productName === ProductNames.Fundraising) {
      return campaign ? 'DonationCampaignFRP' : 'DonationCharityFRP';
    }
    // TODO: Add logic for additional causeType values as required
    return '';
  };

  const attributes = {
    deviceType: getDeviceType(),
    charityId: charity.externalId.toString(),
    marketCode: charity.countryCode,
    causeType: getCauseType(),
  };

  const analyticsCookie =
    typeof document !== 'undefined'
      ? cookies()[clientConfig.analyticsCookieName]
      : undefined;

  if (!analyticsCookie) {
    setAnalyticsCookie(auth);
  }

  return (
    <OptimizelyProvider
      optimizely={optimizelyClient}
      user={{
        id:
          getUserId({ analyticsCookie, jgAuthCookie: auth?.getAuthCookie() }) ??
          v4(),
        attributes,
      }}
    >
      <ForcedEnrolments />
      {children}
    </OptimizelyProvider>
  );
};

function ForcedEnrolments() {
  const [decision] = useDecision('page_content_restructure');
  const { optimizely } = useContext(OptimizelyContext);

  useEffect(() => {
    if (!decision.enabled) return;

    const experimentsCookie = cookies()['jg-experiments'];
    if (!experimentsCookie || !isJson(experimentsCookie)) return;

    const experimentsCookieValue = JSON.parse(experimentsCookie);

    if ('page_content_restructure' in experimentsCookieValue) {
      optimizely?.setForcedVariation(
        'page_content_restructure',
        experimentsCookieValue.page_content_restructure,
      );
    }
  }, [decision.enabled]);

  return null;
}

function setAnalyticsCookie(auth?: Auth | null) {
  const domain = 'domain=.justgiving.com;';
  const path = `path=/;`;
  const maxAge = `max-age=${60 * 60 * 24 * 365 * 10};`; // 10 Years

  if (typeof document !== 'undefined') {
    document.cookie = `${
      clientConfig.analyticsCookieName
    }=logged_in_guid=${getLoggedInGuid(
      auth,
    )}&logged_out_guid=${v4()};${domain}${path}${maxAge}`;
  }
}

function getLoggedInGuid(auth: Auth | null | undefined) {
  if (auth?.isUserLoggedIn()) {
    const userData = auth?.getAccessToken();

    return userData ? userData.guid : '00000000-0000-0000-0000-000000000000';
  }
  return '00000000-0000-0000-0000-000000000000';
}
